import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, RefreshCw, DollarSign } from "lucide-react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Admin — Friend & Family Referral management
 * Canonical Spec v1.2 §8 admin controls + §9 reporting.
 *
 * Route: /admin/friends-and-family (admin only)
 */
export default function AdminFriendsAndFamily() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setErr("");
    try {
      const q = filter === "all" ? "" : `?option=${filter}`;
      const { data } = await api.get(`/admin/friends-and-family${q}`);
      setRows(data.rows || []);
      setTotals(data.totals || {});
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  if (user === null) return null;
  if (!user || user.role !== "admin") {
    return (
      <AppShell>
        <div className="mx-auto max-w-md px-6 py-14 text-center">
          <h1 className="font-display text-xl font-bold mb-2">Admin only</h1>
          <Link to="/" className="btn-primary inline-block mt-4">Back to home</Link>
        </div>
      </AppShell>
    );
  }

  const money = (c) => `$${((c || 0) / 100).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

  const markPaid = async (uid, amount) => {
    if (!window.confirm(`Mark all pending Option B payouts on ${uid} (${money(amount)}) as paid?`)) return;
    try {
      await api.post(`/admin/friends-and-family/${uid}/mark-paid`);
      await load();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
  };

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl px-6 py-10" data-testid="admin-ff-page">
        <div className="flex items-start gap-3 mb-6">
          <div className="p-2 rounded-lg" style={{ background: "var(--ai-bg)" }}>
            <Gift className="w-6 h-6" style={{ color: "var(--ai-text)" }} />
          </div>
          <div>
            <h1 className="font-display text-3xl font-extrabold">Friend &amp; Family Referrals</h1>
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
              Every FF-* UID people have generated to refer friends. Manage Option B ($25) payouts here.
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={load} className="btn-ghost text-sm inline-flex items-center gap-1"
                    data-testid="admin-ff-refresh">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <Link to="/admin/pricing" className="btn-ghost text-sm">Pricing config →</Link>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <TotalCard label="Option B payouts owed" value={money(totals.b_payouts_pending_cents)} tone="pending"
                     testId="admin-ff-total-owed" />
          <TotalCard label="Option B payouts paid lifetime" value={money(totals.b_payouts_paid_cents)} tone="paid"
                     testId="admin-ff-total-paid" />
        </div>

        <div className="flex items-center gap-2 mb-4">
          <label className="text-xs font-semibold uppercase tracking-wide"
                 style={{ color: "var(--text-secondary)" }}>Option</label>
          <select className="field-input" style={{ width: 200 }} value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  data-testid="admin-ff-filter">
            <option value="all">All</option>
            <option value="A">Option A (Gift)</option>
            <option value="B">Option B (Split)</option>
          </select>
        </div>

        {err && (
          <div className="mb-4 p-3 rounded-md text-xs" style={{ background: "#FEF2F2", color: "#991B1B" }}>
            {err}
          </div>
        )}

        {loading ? (
          <div className="text-sm text-center py-16"
               style={{ color: "var(--text-secondary)" }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div className="card-surface p-10 text-center text-sm"
               style={{ color: "var(--text-secondary)" }}>
            No Friend &amp; Family UIDs yet.
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => (
              <div key={r.uid} className="card-surface p-4 flex items-center gap-4"
                   data-testid={`admin-ff-row-${r.uid}`}>
                <code className="font-mono font-bold text-sm px-2 py-1 rounded"
                      style={{ background: "var(--ai-bg)", color: "var(--ai-text)" }}>
                  {r.uid}
                </code>
                <div className="flex-1">
                  <div className="font-display font-bold text-sm">{r.referrer_name}</div>
                  <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
                    {r.referrer_email} · Option {r.option_selected} · {r.attributed_cases} cases
                  </div>
                </div>
                {r.option_selected === "B" && (
                  <div className="text-right">
                    <div className="text-[11px]" style={{ color: "var(--text-secondary)" }}>Owed</div>
                    <div className="font-display font-bold text-lg"
                         style={{ color: "var(--accent-copper)" }}>
                      {money(r.b_payout_pending_cents)}
                    </div>
                  </div>
                )}
                {r.option_selected === "B" && r.b_payout_pending_cents > 0 && (
                  <button className="btn-primary text-xs inline-flex items-center gap-1"
                          onClick={() => markPaid(r.uid, r.b_payout_pending_cents)}
                          data-testid={`admin-ff-mark-paid-${r.uid}`}>
                    <DollarSign className="w-3.5 h-3.5" /> Mark paid
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function TotalCard({ label, value, tone, testId }) {
  const color = tone === "pending" ? "var(--accent-copper)" : "#065F46";
  return (
    <div className="card-surface p-5" data-testid={testId}>
      <div className="text-xs uppercase tracking-wide mb-1"
           style={{ color: "var(--text-secondary)" }}>{label}</div>
      <div className="font-display text-3xl font-extrabold" style={{ color }}>{value}</div>
    </div>
  );
}
